import type { IntegrationName } from "@tellescope/tanstack-query";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { IntegrationConfig } from "../../types";
import { Badge, Button, SearchInput, useUncontrolledState } from "@tellescope/ui";
import { PaginatedList, PaginatedListProps } from "@tellescope/design-system";
import { IntegrationSelectionListItem } from "./IntegrationSelectionListItem";
import { useTranslation } from "react-i18next";
import { getAllIntegrationsTags } from "../../constants";

export interface IntegrationSelectionListProps extends Omit<PaginatedListProps, "children"> {
  options: IntegrationConfig[];
  selectedIntegrations?: IntegrationName[];
  setSelectedIntegrations?: Dispatch<SetStateAction<IntegrationName[]>>;
}

export const IntegrationSelectionList = ({
  options,
  selectedIntegrations: providedSelectedIntegrations,
  setSelectedIntegrations: providedSetSelectedIntegrations,
  ...rest
}: IntegrationSelectionListProps) => {
  const { t } = useTranslation("onboarding");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTag, setSelectedTag] = useState("all");
  const [selectedIntegrations, setSelectedIntegrations] = useUncontrolledState({
    state: providedSelectedIntegrations ?? [],
    setState: providedSetSelectedIntegrations,
  });
  const tags = useMemo(() => {
    const integrationTags = getAllIntegrationsTags(options);
    if (!Object.keys(integrationTags).length) {
      return [];
    }

    return [
      { value: "all", label: t("All ({{amount}})", { amount: options.length }) },
      ...Object.entries(integrationTags).map(([value, label]) => ({
        value,
        label: `${label} (${options.filter(({ tags }) => tags.some((tag) => tag.value === value)).length})`,
      })),
    ];
  }, [options, t]);

  const filteredOptions = useMemo(() => {
    const lowerSearch = searchQuery.toLowerCase();
    const optionsMatchingSearch = searchQuery
      ? options.filter((option) => {
          const searchValue = `${option.tags
            .map(({ label }) => label.toLowerCase())
            .join("|")}|${option.title.toLowerCase()}|${option.description.toLowerCase()}`;
          return searchValue.includes(lowerSearch);
        })
      : options;

    return selectedTag === "all"
      ? optionsMatchingSearch
      : optionsMatchingSearch.filter(({ tags }) => tags.some(({ value }) => value === selectedTag));
  }, [options, searchQuery, selectedTag]);

  return (
    <div className="flex flex-col pt-8 gap-4 h-full">
      <SearchInput
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        wrapperClassName="bg-white shrink-0"
        placeholder={t("Search integrations to connect with")}
      />
      <div className="flex gap-2 flex-wrap">
        {tags.map((tag) => (
          <Button
            key={tag.value}
            asChild
            onClick={() => setSelectedTag(tag.value === "all" ? "all" : tag.value === selectedTag ? "all" : tag.value)}
            className="rounded-full text-xs h-auto py-1 cursor-pointer"
            variant={tag.value === selectedTag ? "default" : "secondary"}
          >
            <Badge variant={tag.value === selectedTag ? "default" : "secondary"}>{tag.label}</Badge>
          </Button>
        ))}
      </div>
      <PaginatedList {...rest} className="grow pr-2">
        {filteredOptions.map((option) => (
          <IntegrationSelectionListItem
            key={option.name}
            name={option.name}
            onClick={
              option.required
                ? undefined
                : () =>
                    setSelectedIntegrations((prev) => {
                      const isSelected = prev.includes(option.name);
                      if (isSelected) {
                        return prev.filter((name) => name !== option.name);
                      }
                      return [...prev, option.name];
                    })
            }
            isSelected={selectedIntegrations.includes(option.name)}
            className="w-full mb-3"
          />
        ))}
      </PaginatedList>
    </div>
  );
};
